import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { ChevronLeft, Download, Ticket, DollarSign, Eye, Users, Clock, MessageCircle, Heart, BarChart3 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { AdminLayout } from '@/components/layout/AdminLayout';
import { mockEvents, mockAnalytics } from '@/lib/mockData';
import { formatCurrency, formatNumber } from '@/lib/utils';

const StatCard = ({ icon: Icon, label, value, color = 'primary' }) => (
  <Card className="bg-card border-border p-6">
    <div className="flex items-center gap-4">
      <div className={`w-12 h-12 rounded-lg bg-${color}/20 flex items-center justify-center`}>
        <Icon className={`w-6 h-6 text-${color}`} />
      </div>
      <div>
        <p className="text-2xl font-bold text-white">{value}</p>
        <p className="text-sm text-muted-foreground">{label}</p>
      </div>
    </div>
  </Card>
);

export function AdminEventAnalytics() {
  const { eventId } = useParams();
  const event = mockEvents.find(e => e.id === eventId) || mockEvents[0];

  return (
    <AdminLayout>
      <div className="p-8">
        <Link to={`/admin/events/${eventId}`} className="inline-flex items-center text-muted-foreground hover:text-white mb-6">
          <ChevronLeft className="w-5 h-5 mr-1" />
          Back to Event
        </Link>

        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-white">+ Analytics Dashboard</h1>
            <p className="text-muted-foreground mt-1">{event.title}</p>
          </div>
          <Button variant="outline" className="border-border">
            <Download className="w-4 h-4 mr-2" />
            EXPORT CSV
          </Button>
        </div>

        {/* Main Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <Card className="bg-card border-border p-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-lg bg-primary/20 flex items-center justify-center">
                <Ticket className="w-6 h-6 text-primary" />
              </div>
              <div>
                <p className="text-3xl font-bold text-white">{formatNumber(mockAnalytics.ticketsSold)}</p>
                <p className="text-sm text-muted-foreground">Tickets Sold</p>
              </div>
            </div>
          </Card>

          <Card className="bg-card border-border p-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-lg bg-green-500/20 flex items-center justify-center">
                <DollarSign className="w-6 h-6 text-green-500" />
              </div>
              <div>
                <p className="text-3xl font-bold text-white">{formatCurrency(mockAnalytics.revenue)}</p>
                <p className="text-sm text-muted-foreground">Revenue</p>
              </div>
            </div>
          </Card>

          <Card className="bg-card border-border p-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-lg bg-blue-500/20 flex items-center justify-center">
                <Eye className="w-6 h-6 text-blue-500" />
              </div>
              <div>
                <p className="text-3xl font-bold text-white">{formatNumber(mockAnalytics.uniqueViewers)}</p>
                <p className="text-sm text-muted-foreground">Unique Viewers</p>
              </div>
            </div>
          </Card>

          <Card className="bg-card border-border p-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-lg bg-purple-500/20 flex items-center justify-center">
                <Users className="w-6 h-6 text-purple-500" />
              </div>
              <div>
                <p className="text-3xl font-bold text-white">{formatNumber(mockAnalytics.peakConcurrency)}</p>
                <p className="text-sm text-muted-foreground">Peak Concurrency</p>
              </div>
            </div>
          </Card>
        </div>

        {/* Secondary Stats */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <Card className="bg-card border-border p-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-lg bg-orange-500/20 flex items-center justify-center">
                <Clock className="w-6 h-6 text-orange-500" />
              </div>
              <div>
                <p className="text-3xl font-bold text-white">{mockAnalytics.watchTime}</p>
                <p className="text-sm text-muted-foreground">Watch Time</p>
              </div>
            </div>
          </Card>

          <Card className="bg-card border-border p-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-lg bg-pink-500/20 flex items-center justify-center">
                <MessageCircle className="w-6 h-6 text-pink-500" />
              </div>
              <div>
                <p className="text-3xl font-bold text-white">{formatNumber(mockAnalytics.chatCount)}</p>
                <p className="text-sm text-muted-foreground">Chat Count</p>
              </div>
            </div>
          </Card>

          <Card className="bg-card border-border p-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-lg bg-accent/20 flex items-center justify-center">
                <Heart className="w-6 h-6 text-accent" />
              </div>
              <div>
                <p className="text-3xl font-bold text-white">{formatCurrency(mockAnalytics.tips)}</p>
                <p className="text-sm text-muted-foreground">Tips</p>
              </div>
            </div>
          </Card>

          <Card className="bg-card border-border p-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-lg bg-cyan-500/20 flex items-center justify-center">
                <BarChart3 className="w-6 h-6 text-cyan-500" />
              </div>
              <div>
                <p className="text-3xl font-bold text-white">{formatNumber(mockAnalytics.sponsorImpressions)}</p>
                <p className="text-sm text-muted-foreground">Sponsor Impressions</p>
              </div>
            </div>
          </Card>
        </div>

        {/* Performance Summary */}
        <Card className="bg-card border-border p-6">
          <h2 className="text-xl font-bold text-white mb-4">Performance Summary</h2>
          <p className="text-muted-foreground">
            This event performed exceptionally well with {formatNumber(mockAnalytics.ticketsSold)} tickets sold 
            generating {formatCurrency(mockAnalytics.revenue)} in revenue. The livestream attracted 
            {formatNumber(mockAnalytics.uniqueViewers)} unique viewers with a peak concurrent audience of 
            {formatNumber(mockAnalytics.peakConcurrency)}. Viewer engagement was strong with 
            {formatNumber(mockAnalytics.chatCount)} chat messages and {formatCurrency(mockAnalytics.tips)} in tips.
          </p>
        </Card>
      </div>
    </AdminLayout>
  );
}

export default AdminEventAnalytics;
